import { findNode, findNodeParent } from "@/lib/tree/tree";
import { SingularityUIState } from "@/store/modules/singularity";
import { documentStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import {
  isImmediateChildOfContainerThatSupportsRepeatingChildren,
  isWrapperLink
} from "@/futurelayer/canvas/canvas-controls/grid-and-link-wrapper-detection";

export type FutureLayerElementBehavior = {
  canActivate: true | false;
  initialPanel: SingularityUIState["singularityLeftPanel"] | "none";
  setPanelState: (element: TreeNode) => void;
};

export const DEFAULT_ELEMENT_BEHAVIOR: FutureLayerElementBehavior = {
  canActivate: true,
  initialPanel: "content_properties_editor",
  setPanelState: () => {}
};

export const DISABLED_ELEMENT_BEHAVIOR: FutureLayerElementBehavior = {
  canActivate: false,
  initialPanel: "none",
  setPanelState: () => {}
};

const elementBehaviorRegistry = new Map<string, FutureLayerElementBehavior>();

export function registerElementBehavior(
  slug: string | string[],
  behavior: FutureLayerElementBehavior
): void {
  if (Array.isArray(slug)) {
    slug.forEach(s => elementBehaviorRegistry.set(s, behavior));
    return;
  } else {
    elementBehaviorRegistry.set(slug, behavior);
  }
}

export function getFutureLayerElementBehavior(
  slug: string
): FutureLayerElementBehavior {
  return elementBehaviorRegistry.get(slug) ?? DEFAULT_ELEMENT_BEHAVIOR;
}

export function doesFutureLayerElementBehaviorAllowElementToBeActivated(
  elementId: TreeNode["id"]
) {
  const node = findNode(documentStore.document.tree, elementId);
  if (!node) return false;
  return getFutureLayerElementBehavior(node.data.type).canActivate;
}

export function getNearestNodeThatCanBeActivatedInFutureLayerMode(
  node: TreeNode
): TreeNode | null {
  let current = node;
  while (current) {
    if (getFutureLayerElementBehavior(current.data.type).canActivate) {
      return current;
    }

    if (isImmediateChildOfContainerThatSupportsRepeatingChildren(current)) {
      return null;
    }

    if (isWrapperLink(current)) {
      return null;
    }

    const parent = findNodeParent(documentStore.document.tree, current);
    if (!parent) break;
    current = parent;
  }
  return null;
}

export function getNearestElementIdThatCanBeActivatedInFutureLayerMode(
  elementId: TreeNode["id"]
): TreeNode["id"] | null {
  const node = findNode(documentStore.document.tree, elementId);
  if (!node) return null;

  const nearestElementId =
    getNearestNodeThatCanBeActivatedInFutureLayerMode(node)?.id;
  return nearestElementId ? nearestElementId : null;
}
